import forge from "node-forge";
import { v4, Version4Options } from "uuid";
import {
  getProviderDecimals,
  getProviderConfig,
  CreditProviderId,
} from "../config/creditRegistry";
import { CardStatusResponse, KYCStatus, RainStatus } from "./types";
import React from "react";
import USDT0Icon from "../components/icons/USDT0Icon";
import USDeIcon from "../components/icons/USDeIcon";
import USDhlIcon from "../components/icons/USDhlIcon";

// Format Units utility function (simplified version)
export const formatUnits = (value: bigint, decimals: number): string => {
  const divisor = BigInt(10 ** decimals);
  const quotient = value / divisor;
  const remainder = value % divisor;

  const formattedRemainder = remainder.toString().padStart(decimals, "0");
  return `${quotient.toString()}.${formattedRemainder}`;
};

// --- Helper: resolve decimals ---
function resolveDecimals(
  creditType: CreditProviderId,
  baseTokenAddress?: string
): number {
  if (baseTokenAddress) {
    const token = VAULT_TOKENS[baseTokenAddress.toLowerCase()];
    if (token) return token.decimals;
  }
  const decimals = getProviderDecimals(creditType);
  return decimals ?? 6; // Default to 6 decimals if provider not found
}

// --- Main: format available limit ---
// Helper function for formatting credit limits
export function formatAvailableLimit(
  availableLimit: number,
  creditType: CreditProviderId,
  baseTokenAddress?: string
): number {
  const decimals = resolveDecimals(creditType, baseTokenAddress);
  const formatted = formatUnits(BigInt(availableLimit), decimals);
  return parseFloat(Number(formatted).toFixed(4));
}

// Mapper function to convert Euler metadata to include suppliedAssets
export function mapEulerMetadataToSuppliedAssets(metadata: any) {
  if (!Array.isArray(metadata?.vaults)) return metadata;

  const suppliedAssets = metadata.vaults.map((vault: any) => {
    const formattedAsset = Number(formatUnits(BigInt(vault.assets || 0), 18));

    return {
      UsageAsCollateralEnabledOnUser: true, // Assume true for Euler
      amount: formattedAsset,
      amountUSD: 0, // No USD value provided, set to 0 or calculate if possible
      collateralAmountBase: String(formattedAsset),
      collateralAmountBig: formattedAsset,
      supplyRate: 0, // Not provided by Euler, set to 0 or fetch if available
      underlyingAsset: vault.vault || "",
    };
  });

  const suppliedValue = suppliedAssets.reduce(
    (sum: number, asset: any) => sum + asset.amountUSD,
    0
  );

  return {
    ...metadata,
    suppliedAssets,
    suppliedValue,
  };
}

export const waitFor = async (ms = 1500) =>
  await new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Encrypts a PIN using AES-GCM with a session key provided by Rain.
 *
 * @param pin - The raw numeric PIN (e.g. "1234")
 * @param sessionKey - Hex-encoded session key from Rain
 * @returns An object containing base64-encoded IV and encrypted data (with tag)
 */
export function encryptPinWithSessionKey(
  pin: string,
  sessionKey: string
): {
  iv: string;
  data: string;
} {
  // Step 1: Format PIN as `2{lenInHex}{PIN}{F-padding}`
  const hexLength = pin.length.toString(16);
  const formattedPin = `2${hexLength}${pin}${"F".repeat(14 - pin.length)}`;

  // Step 2: Generate 16-byte IV
  const ivBytes = forge.random.getBytesSync(16);
  const encodedIV = forge.util.encode64(ivBytes);

  // Step 3: Convert sessionKey to bytes
  const keyBytes = forge.util.hexToBytes(sessionKey);

  // Step 4: Create AES-GCM cipher
  const cipher = forge.cipher.createCipher("AES-GCM", keyBytes);
  cipher.start({ iv: ivBytes });

  // Step 5: Encrypt the formatted PIN
  cipher.update(forge.util.createBuffer(formattedPin));
  cipher.finish();

  // Step 6: Concatenate ciphertext + tag, then base64 encode
  const encrypted = cipher.output.getBytes() + cipher.mode.tag.getBytes();
  const encodedEncrypted = forge.util.encode64(encrypted);

  return {
    iv: encodedIV,
    data: encodedEncrypted,
  };
}

export const generateUUID = (options?: Version4Options) => {
  return v4(options);
};

/**
 * Returns the appropriate cardSolverConfig object for a given creditType.
 * Used to construct the payload for card creation.
 *
 * @param creditType - The type of credit from registry
 * @returns The cardSolverConfig object for the specified creditType
 */
export function getCardSolverConfig(
  creditType: string
): Record<string, string> {
  const config = getProviderConfig(creditType as CreditProviderId);
  return config ?? {}; // Return empty object if provider not found
}

export const mapNewStatusToLegacyCardStatus = (
  kycStatus: KYCStatus,
  rainStatus: RainStatus,
  errorDetails?: string
): CardStatusResponse["data"] => {
  if (errorDetails) {
    return "error";
  }

  if (rainStatus === "approved") {
    return "approved"; // show create card now
  }

  // Map based on KYC and Rain status combinations
  if (kycStatus === "not_requested" || kycStatus === "requested") {
    return "userNotExisting"; // show sumsub widget
  }

  if (kycStatus === "rejected") {
    return "rejected"; // show rejected screen
  }

  if (kycStatus === "resubmitted" || kycStatus === "onhold") {
    return "resubmit"; // show resubmission screen
  }

  if (kycStatus === "pending") {
    return "pending"; // pending screen
  }

  if (kycStatus === "approved") {
    if (
      rainStatus === "review" ||
      rainStatus === "pending" ||
      rainStatus === "initiated"
    ) {
      return "pending"; // show pending screen
    }

    if (rainStatus === "rejected") {
      return "rejected"; // show rejected screen
    }
  }

  return "initiated";
};

const USDHL_ADDRESS = "0xb50a96253abdf803d85efcdce07ad8becbc52bd5";
const USDE_ADDRESS = "0x5d3a1ff2b6bab83b63cd9ad0787074081a52ef34";
const USDT0_ADDRESS = "0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb";

export const VAULT_TOKENS: Record<
  string,
  { symbol: string; decimals: number }
> = {
  [USDT0_ADDRESS]: { symbol: "USD₮0", decimals: 6 },
  [USDE_ADDRESS]: { symbol: "USDe", decimals: 18 },
  [USDHL_ADDRESS]: { symbol: "USDhl", decimals: 6 },
};

export const getVaultTokenIcon = (
  size: number = 26
): Record<
  string,
  { icon: React.JSX.Element; symbol: string; decimals: number }
> => ({
  [USDHL_ADDRESS]: {
    icon: React.createElement(USDhlIcon, { width: size, height: size }),
    symbol: "USDhl",
    decimals: 6,
  },
  [USDE_ADDRESS]: {
    icon: React.createElement(USDeIcon, { width: size, height: size }),
    symbol: "USDe",
    decimals: 18,
  },
  [USDT0_ADDRESS]: {
    icon: React.createElement(USDT0Icon, { width: size, height: size }),
    symbol: "USD₮0",
    decimals: 6,
  },
});
